// 購入履歴の「もう一度購入する」を、カートの小窓ではなく商品ページ /product/{商品} へ移す（Issue #86）。
//
// 背景（2026-09-29 定例）: このボタンはおちゃのこの JS が小窓（商品フォーム）を開き、商品ページを通らずに
// カートへ入る。在庫を超える数量がそのまま入るケースが週 1〜2 回あり、商品ページの在庫表示とカート制御
// （hooks/stock-check.ts）をすり抜けていた。→ 商品ページへ移らせ、在庫と価格はそこで確かめてもらう。
//
// - 目印は a.repurchaseinput（実測 2026-09-30 fukuro.in /sales-history-detail/{id}）。href は
//   /product/{商品}/v/{バリエーション}。小窓は class の open_modal_window_product_form を見ておちゃのこの
//   common-min.js が開く（handler の付き先は不明）ので、document の capture で先に止める。
// - ★★ 移り先は /v/… を外した /product/{商品}。stock-check の when は /^\/product\/\d+\/?$/ で、
//   /v/ 付きでは在庫制御が動かない（そのまま飛ばすとすり抜けが残る）。stock-check の when は変えない。
// - 対象店は shared/hooks.ts の STOCK_CHECK_SITES（すり抜けが問題になる店）。文言・見た目は変えない。
// - おちゃのこがテンプレートを作り直して目印が消えると黙って空振りになるので、目印が無いのに文言だけある
//   ときは console.warn を出す（握りつぶさない）。

export const REPURCHASE_SELECTOR = 'a.repurchaseinput'
export const REPURCHASE_LABEL = 'もう一度購入する'

const PRODUCT_PATH = /^\/product\/(\d+)(?:\/v\/[^/]*)?\/?$/

/**
 * 「もう一度購入する」の href から移り先を作る。/product/{商品}（/v/… は外す）。
 * 同じ店のホストの商品ページでなければ null（その場合は横取りせず、おちゃのこの動きに任せる）。
 */
export function repurchaseTarget(href: string | null | undefined, base: string): string | null {
  if (!href) return null
  let url: URL
  try {
    url = new URL(href, base)
  } catch {
    return null
  }
  if (url.host !== new URL(base).host) return null
  const m = PRODUCT_PATH.exec(url.pathname)
  return m ? `/product/${m[1]}` : null
}

/** 目印（a.repurchaseinput）が無いのに文言だけあるか。テンプレートが変わって空振りしている合図 */
export function markerMissing(root: ParentNode & { textContent: string | null }): boolean {
  return !root.querySelector(REPURCHASE_SELECTOR) && (root.textContent ?? '').includes(REPURCHASE_LABEL)
}

interface ClickLike {
  target: EventTarget | null
  button?: number
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
  preventDefault(): void
  stopImmediatePropagation(): void
}

/**
 * クリック 1 回ぶんの処理。a.repurchaseinput なら小窓を止めて移り先へ。処理したら移り先を返す。
 * Ctrl/⌘/Shift などの修飾クリックは href を書き換えるだけでブラウザの既定動作（別タブ等）に任せる。
 */
export function handleRepurchaseClick(ev: ClickLike, base: string, navigate: (url: string) => void): string | null {
  const t = ev.target as Element | null
  const a = t && typeof t.closest === 'function' ? t.closest(REPURCHASE_SELECTOR) : null
  if (!a) return null
  const target = repurchaseTarget(a.getAttribute('href'), base)
  if (!target) return null
  ev.stopImmediatePropagation()
  a.setAttribute('href', target)
  if ((ev.button ?? 0) !== 0 || ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey) return target
  ev.preventDefault()
  navigate(target)
  return target
}

export function installRepurchaseToProduct(doc: Document, base: string, navigate: (url: string) => void): void {
  doc.addEventListener('click', (ev) => handleRepurchaseClick(ev as MouseEvent, base, navigate), true)
  if (doc.body && markerMissing(doc.body)) {
    console.warn(`[wu] repurchase-to-product: 「${REPURCHASE_LABEL}」はあるが ${REPURCHASE_SELECTOR} が見つかりません（おちゃのこのテンプレート変更?）`)
  }
}

export default function repurchaseToProduct(): void {
  installRepurchaseToProduct(document, location.href, (url) => location.assign(url))
}
